import { MatcherView, PhaseData, useMatcher } from "utils/MatcherProvider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMatchPhaseMutation, useFinishMatchMutation } from "graphql/types/graphql";
import { enqueueSnackbar } from "notistack";
import { context } from "utils/Provider";
import { useContext, useState } from "react";
import enqueueError from "utils/enqueueError";
import { useNavigate } from "react-router-dom";
import { Title } from "../styles/Title";
import { Stack, styled, TextField } from "@mui/material";
import { DonutChart } from "./DonutChart";
import colors from "assets/theme-dark/base/colors";

export function Results() {
  const { phaseData, setPhaseData, phaseHistory, setPhaseHistory, changeView } = useMatcher();
  const { setIsLoading, selectedCube } = useContext(context);
  const navigate = useNavigate();
  const [outputViewName, setOutputViewName] = useState("");

  const [match, { data, loading: matchLoading, error: matchError }] = useMatchPhaseMutation({
    update: (cache, { data }) => {
      if (data?.matchPhase?.success) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "queryMatchedViewsPhaseData" });
        cache.gc();
      }
    },
    onCompleted: (data) => {
      const phase: PhaseData = {
        ...phaseData,
        phase: data.matchPhase.statistics.phase,
        matchedCount: data.matchPhase.statistics.matchedCount,
        duplicateCount: data.matchPhase.statistics.duplicateCount,
        totalRecords: data.matchPhase.statistics.totalRecords,
        duplicates: data.matchPhase.statistics.duplicates,
      };
      setPhaseData(phase);

      setPhaseHistory((current) => [...current, phase]);

      enqueueSnackbar("Tables matched successfully", { variant: "success" });
      setIsLoading(false);
    },
    onError: (error) => {
      enqueueError(error);
      setIsLoading(false);
    },
  });

  const [finishMatch, { loading: finishLoading }] = useFinishMatchMutation({
    update: (cache, { data }) => {
      if (data?.finishMatch.success) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "queryProcessedDataViews" });
        cache.gc();
      }
    },
    onCompleted: (data) => {
      if (data.finishMatch.success) {
        enqueueSnackbar("Matching finalized successfully", { variant: "success" });
        setIsLoading(false);
        setOutputViewName("");
        changeView(MatcherView.SUMMARY);
      } else {
        enqueueSnackbar(`Failed to finalize matching: ${data.finishMatch.error}`, { variant: "error" });
        setIsLoading(false);
      }
    },
    onError: (error) => {
      enqueueError(error);
      setIsLoading(false);
    },
  });

  const handleMatchPhases = async () => {
    try {
      setIsLoading(true);
      await match({
        variables: {
          cubeId: selectedCube.cubeId,
          phase: phaseData.phase + 1,
          tableName: phaseData.tableName,
          matcherTable: phaseData.matcherTable,
          identifier: phaseData.identifier,
          headers: phaseData.headers,
        },
      });
    } catch (error: any) {
      enqueueSnackbar(`Error matching tables: ${error.message}`, { variant: "error", autoHideDuration: 5000 });
      setIsLoading(false);
    }
  };

  const handleFinishMatch = async () => {
    try {
      setIsLoading(true);
      await finishMatch({
        variables: {
          cubeId: selectedCube.cubeId,
          tableName: phaseData.tableName,
          outputViewName,
          matcherTableName: phaseData.matcherTable,
          identifier: phaseData.identifier,
        },
      });
    } catch (error) {
      enqueueSnackbar(`Error finalizing match: ${error instanceof Error ? error.message : "Something went wrong"}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Column>
        <MDTypography mb="2rem" variant="h3">
          Phase Report
        </MDTypography>
        <MDTypography>
          <Title>Phase:</Title> {phaseData.phase + 1}
        </MDTypography>
        <MDTypography>
          <Title>Table Name:</Title> {phaseData.tableName}
        </MDTypography>
        <MDTypography>
          <Title>Matcher Table:</Title> {phaseData.matcherTable}
        </MDTypography>
        <MDTypography>
          <Title>Identifier:</Title> {phaseData.identifier}
        </MDTypography>
        <MDTypography>
          <Title>Matched:</Title> {phaseData.matchedCount}
        </MDTypography>
        <MDTypography>
          <Title>Duplicates:</Title> {phaseData.duplicateCount}
        </MDTypography>
        {phaseData.headers.length <= phaseData.phase + 1 && (
          <MDBox mt="1rem">
            <TextField
              label="Finalized View Name"
              placeholder="Enter a name for the finalized view"
              variant="outlined"
              size="small"
              value={outputViewName}
              onChange={(e) => setOutputViewName(e.target.value)}
              sx={{
                width: "300px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: colors.grey[500],
                  },
                  "&:hover fieldset": {
                    borderColor: colors.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary.main,
                  },
                  color: colors.text.main,
                },
                "& .MuiInputBase-input": {
                  color: colors.text.main,
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[500],
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.primary.main,
                },
              }}
            />
          </MDBox>
        )}
        <MDBox mt="3rem" display={"flex"} flexDirection={"row"} gap={2}>
          {phaseData.matchedCount > 0 && (
            <MDButton
              disabled={matchLoading || finishLoading}
              variant="background"
              sx={{ maxWidth: "200px", borderRadius: "8px" }}
              onClick={() => changeView(MatcherView.UNMATCH)}
            >
              Unmatch data
            </MDButton>
          )}
          {phaseData.duplicateCount > 0 && (
            <MDButton
              disabled={matchLoading || finishLoading}
              variant="background"
              sx={{ maxWidth: "200px", borderRadius: "8px" }}
              onClick={() => changeView(MatcherView.DUPLICATES)}
            >
              View Duplicates
            </MDButton>
          )}
          {phaseData.headers.length > phaseData.phase + 1 ? (
            <MDButton
              disabled={matchLoading || finishLoading}
              loading={matchLoading}
              variant="background"
              sx={{ maxWidth: "200px", borderRadius: "8px" }}
              onClick={() => handleMatchPhases()}
            >
              Next Phase
            </MDButton>
          ) : (
            <MDButton
              disabled={matchLoading || finishLoading || !outputViewName.trim()}
              loading={finishLoading}
              variant="background"
              color="secondary"
              sx={{ maxWidth: "200px", borderRadius: "8px" }}
              onClick={handleFinishMatch}
            >
              Finish matching
            </MDButton>
          )}
        </MDBox>
      </Column>
      <Column style={{ flex: 1.6 }}>
        <DonutChart
          phaseData={phaseData}
          phaseDataBefore={phaseHistory.length > 1 && phaseHistory[phaseData.phase - 1]}
        />
      </Column>
    </Container>
  );
}

const Container = styled(Stack)`
  flex-direction: row;
  gap: 1rem;
  flex: 1;
`;
const Column = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
